import { FormEvent, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import { MdsPanelContainer } from "@/design-system/components/panel";
import { useAppStore } from "@/store";
import { AudioRecordingCoordinator } from "@/store/audio-capture/coordinator";
import { uuidModule } from "@/modules/uuid";
import { clientEnvModule } from "@/modules/client-env";
import { MDSButton, MDSText, MDSTextVariant, MDSTextColor } from "@/mds";

interface FormState {
  noteId: string;
  capturedAudioId: string;
}

export const AudioRecordingCreatePage = observer(() => {
  const { store } = useAppStore();
  const coordinator = useMemo(
    () => new AudioRecordingCoordinator(store.memDb.audioRecordings),
    [store.memDb.audioRecordings]
  );

  const [form, setForm] = useState<FormState>({ noteId: "", capturedAudioId: "" });
  const [creating, setCreating] = useState(false);
  const [creationError, setCreationError] = useState<string | null>(null);

  const handleChange = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creating) return;

    setCreating(true);
    setCreationError(null);

    try {
      const recordingId = uuidModule.generate();
      const capturedAudioId = form.capturedAudioId || uuidModule.generate();
      const engine = clientEnvModule.isDesktop() ? "DESKTOP" : "WEB";

      const snapshot = await coordinator.startRecording({
        recordingId,
        noteId: form.noteId || undefined,
        capturedAudioId,
        engine,
      });

      if (!snapshot) {
        throw new Error("Failed to create recording run");
      }

      setForm({ noteId: "", capturedAudioId: "" });
      store.navigation.goToAudioRecording({ recordingId: recordingId });
    } catch (err) {
      setCreationError(err instanceof Error ? err.message : "Failed to create recording");
      setCreating(false);
      return;
    }

    setCreating(false);
  };

  return (
    <MdsPanelContainer>
      <h1>Create Audio Recording</h1>
      <MDSText variant={MDSTextVariant.TextSm}>
        Generate an empty recording run that can be associated with a note later.
      </MDSText>
      <form onSubmit={handleSubmit} style={{ display: "grid", gap: "0.75rem", maxWidth: 360 }}>
        <label>
          <MDSText variant={MDSTextVariant.TextSm}>Note ID (optional)</MDSText>
          <input
            type="text"
            value={form.noteId}
            onChange={handleChange("noteId")}
            placeholder="note_..."
          />
        </label>
        <label>
          <MDSText variant={MDSTextVariant.TextSm}>Captured Audio ID (optional)</MDSText>
          <input
            type="text"
            value={form.capturedAudioId}
            onChange={handleChange("capturedAudioId")}
            placeholder="auto-generated if blank"
          />
        </label>
        <MDSButton type="submit" isDisabled={creating}>
          {creating ? "Creating..." : "Create Recording"}
        </MDSButton>
        {creationError && (
          <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Danger}>
            {creationError}
          </MDSText>
        )}
      </form>
    </MdsPanelContainer>
  );
});

export default AudioRecordingCreatePage;
